import { useEffect, useState } from 'react'
import { Bookmark, ChevronLeft, TriangleAlert } from 'lucide-react'
import type { Recipe } from '../../shared/types.ts'
import { cx, recipeToMarkdown } from '../lib/util.ts'
import { DishPhoto } from './DishPhoto.tsx'
import { RecipeTags } from './RecipeTags.tsx'

const MARK = { have: '●', partial: '◐', missing: '○' } as const
const STROKE = 2.6

/**
 * Full-screen recipe, sized for reading at arm's length with a pan going.
 * Ingredients and steps tick off as you work through them.
 */
export function RecipeSheet({ recipe, saved, onClose, onToggleSave }: {
  recipe: Recipe
  saved: boolean
  onClose: () => void
  onToggleSave: () => void
}) {
  const [doneSteps, setDoneSteps] = useState<Set<number>>(new Set())
  const [gotItems, setGotItems] = useState<Set<number>>(new Set())
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = '' }
  }, [onClose])

  const toggle = (set: Set<number>, i: number) => {
    const next = new Set(set)
    next.has(i) ? next.delete(i) : next.add(i)
    return next
  }

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(recipeToMarkdown(recipe))
      setCopied(true)
      setTimeout(() => setCopied(false), 1600)
    } catch { /* clipboard blocked — the button just does nothing */ }
  }

  const stats: Array<[string, string]> = [
    [String(recipe.totalMinutes), 'minutes'],
    [String(recipe.activeMinutes), 'hands on'],
    [String(recipe.servings), 'serves'],
  ]
  if (recipe.fitScore != null) stats.push([String(recipe.fitScore), 'fit'])

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet full" onClick={(e) => e.stopPropagation()} role="dialog" aria-label={recipe.title}>
        <div className="reader noscroll">
          <div className="reader-hero">
            <DishPhoto src={recipe.photo} alt="" className="fill" />
            <div className="reader-scrim" />
            <div className="reader-float">
              <button className="back" onClick={onClose} aria-label="Back">
                <ChevronLeft size={21} strokeWidth={STROKE} />
              </button>
              <button className="save" onClick={onToggleSave} aria-label={saved ? 'Remove from saved' : 'Save recipe'}>
                <Bookmark size={19} strokeWidth={STROKE} fill={saved ? 'currentColor' : 'none'} />
              </button>
            </div>
            <div className="reader-head">
              <RecipeTags cuisine={recipe.cuisine} tags={recipe.tags} onDark />
              <h1>{recipe.title}</h1>
            </div>
          </div>

          <div className="reader-body">
            {recipe.blurb && <p className="lede">{recipe.blurb}</p>}

            <div className="stats">
              {stats.map(([value, label]) => (
                <div className="stat" key={label}><b>{value}</b><span>{label}</span></div>
              ))}
            </div>

            {recipe.fitReason && <p className="fit-reason">{recipe.fitReason}</p>}

            {recipe.warnings?.length ? (
              <div className="warn-strip">
                <TriangleAlert size={16} strokeWidth={STROKE} />
                <span><b>Before you start.</b> {recipe.warnings.join(' ')}</span>
              </div>
            ) : null}

            <h4 className="sec-head">Ingredients</h4>
            <div className="ings">
              {recipe.ingredients.map((ing, i) => (
                <button
                  key={i}
                  className={cx('ing', ing.status, gotItems.has(i) && 'got')}
                  onClick={() => setGotItems((s) => toggle(s, i))}
                >
                  <span className="mark">{ing.status ? MARK[ing.status] : '·'}</span>
                  <span>
                    {ing.amount && <b>{ing.amount} </b>}
                    {ing.item}
                    {ing.note && <span className="note">{ing.note}</span>}
                  </span>
                </button>
              ))}
            </div>

            {recipe.shoppingList?.length ? (
              <div className="panel panel-shop">
                <h4 className="sec-head">You'll need to buy</h4>
                <ul>{recipe.shoppingList.map((s, i) => <li key={i}>{s}</li>)}</ul>
              </div>
            ) : null}

            <h4 className="sec-head">Method</h4>
            <div className="steps">
              {recipe.steps.map((s, i) => (
                <button
                  key={i}
                  className={cx('step', doneSteps.has(i) && 'done')}
                  onClick={() => setDoneSteps((d) => toggle(d, i))}
                >
                  <span className="n">{doneSteps.has(i) ? '✓' : i + 1}</span>
                  <span>{s}</span>
                </button>
              ))}
            </div>

            {recipe.tips.length > 0 && (
              <div className="panel panel-tips">
                <h4 className="sec-head">Tips</h4>
                <ul>{recipe.tips.map((t, i) => <li key={i}>{t}</li>)}</ul>
              </div>
            )}

            {recipe.sources.length > 0 && (
              <div className="sources">
                <h4 className="sec-head">Sources</h4>
                {recipe.sources.map((s) => (
                  <a key={s.url} className="source-link" href={s.url} target="_blank" rel="noreferrer noopener">{s.title}</a>
                ))}
              </div>
            )}

            <div className="reader-actions">
              <button className="btn btn-secondary btn-block" onClick={() => void copy()}>
                {copied ? 'Copied ✓' : 'Copy as text'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
